import {
  ForemanEquipmentDispatch,
  ForemanEquipmentUsage,
} from '@ups/xplat/api/dto';
import { Guid } from '@ups/xplat/core';
import moment from 'moment';
import type { ForemanJobService } from './foreman-job.service';

export function bulkUpdateEquipment(
  service: ForemanJobService,
  workOrderId: string,
  shift: number,
  hours: number
) {
  const currentJob = service.selectedJob();
  const equipments = service.getEquipmentForTimeEntry();

  for (const equipment of equipments) {
    const foremanPhaseID = workOrderId || equipment.foremanPhaseID;
    if (!foremanPhaseID) continue;
    updateEquipmentUsages(
      service,
      equipment,
      foremanPhaseID,
      shift,
      hours,
      null,
      true
    );
  }

  service.selectedJob.set({ ...currentJob });
}

export function updateEquipmentUsages(
  service: ForemanJobService,
  equipment: ForemanEquipmentDispatch,
  foremanPhaseID: string,
  shift: number,
  hours: number,
  notes: string,
  isBulk?: boolean
) {
  if (!equipment.usages) {
    equipment.usages = [];
  }

  if (!equipment.foremanPhaseID) {
    equipment.foremanPhaseID = foremanPhaseID;
    const workOrder = service.getWorkOrderById(foremanPhaseID);
    equipment.foremanPhaseName =
      workOrder?.phaseName || equipment.foremanPhaseName;
  }

  let usage = (equipment.usages || []).find(
    (l) =>
      moment(l.dateUsed).format('MM/DD/YYYY') ===
        moment(service.selectedDate()).format('MM/DD/YYYY') &&
      l.shift === shift &&
      l.foremanJobCrewID === service.selectedForemanCrewID() &&
      l.foremanPhaseID === foremanPhaseID
  );

  if (!usage && hours > 0) {
    usage = {
      foremanEquipmentUsageID: Guid.newGuid(),
      foremanEquipmentDispatchID: equipment.foremanEquipmentDispatchID,
      dateUsed: service.selectedDate(),
      foremanPhaseID,
      shift,
      foremanJobCrewID: service.selectedForemanCrewID(),
      quantity: 1,
      isChanged: true,
    } as ForemanEquipmentUsage;

    equipment.usages.push(usage);
  }

  if (usage) {
    const newAmount = hours || usage.amount;
    const newActive = hours > 0;

    usage.isChanged =
      usage.isChanged ||
      usage.amount !== newAmount ||
      usage.foremanNotes !== notes ||
      usage.bActive !== newActive;

    usage.amount = newAmount;
    usage.bActive = newActive;

    if (!isBulk) {
      usage.foremanNotes = notes;
    }
  }

  service.calculateTotalHoursUsed(equipment);

  if (!isBulk) {
    service.updateEquipment(equipment);
  }
}

export function setNotUsed(
  service: ForemanJobService,
  equipment: ForemanEquipmentDispatch
) {
  if (!equipment.usages) {
    equipment.usages = [];
  }

  const selectedForemanCrewID = service.selectedForemanCrewID();
  const usages = service.getEquipmentUsagesForDate(equipment);

  usages.forEach((u) => {
    if (u.bActive) {
      u.bActive = false;
      u.isChanged = true;
    }
  });

  let notUsedUsageRecord = usages.find((u) => u.notUsed === true);

  if (!notUsedUsageRecord) {
    notUsedUsageRecord = {
      foremanEquipmentUsageID: Guid.newGuid(),
      foremanEquipmentDispatchID: equipment.foremanEquipmentDispatchID,
      dateUsed: service.selectedDate(),
      foremanJobCrewID: selectedForemanCrewID,
      shift: service.selectedShift(),
      isChanged: true,
    } as ForemanEquipmentUsage;

    equipment.usages.push(notUsedUsageRecord);
  } else if (
    notUsedUsageRecord.bActive !== true ||
    notUsedUsageRecord.notUsed !== true
  ) {
    notUsedUsageRecord.isChanged = true;
  }

  notUsedUsageRecord.bActive = true;
  notUsedUsageRecord.notUsed = true;

  service.calculateTotalHoursUsed(equipment);
  service.updateEquipment(equipment);
}

export function clearNotUsed(
  service: ForemanJobService,
  equipment: ForemanEquipmentDispatch
) {
  if (!equipment.usages) {
    equipment.usages = [];
  }

  service.selectedEquipmentUsages().forEach((u) => {
    if (u.notUsed === true && u.bActive) {
      u.bActive = false;
      u.isChanged = true;
    }
  });

  service.calculateTotalHoursUsed(equipment);
  service.updateEquipment(equipment);
}

export function recalculateEquipmentTotalHours(service: ForemanJobService) {
  const currentJob = service.selectedJob();
  if (!currentJob || !currentJob.equipment) return;

  const targetDate = service.selectedDate() || new Date();

  for (const equipment of currentJob.equipment) {
    const usages = (equipment.usages || []).filter((l) => {
      if (!l.dateUsed) return false;
      return new Date(l.dateUsed).toDateString() === targetDate.toDateString();
    });

    equipment.totalHoursUsed = usages
      .filter((l) => !!l.bActive && !l.notUsed)
      .reduce((acc, n) => {
        const quantity = Number(n.quantity || 1);
        const amount = Number(n.amount || 0);
        return acc + quantity * amount;
      }, 0);
  }

  service.selectedJob.set({ ...currentJob });
}

export function updateSelectedEquipment(
  service: ForemanJobService,
  equipmentDispatchID: string,
  updates: Partial<ForemanEquipmentDispatch>
) {
  const currentJob = service.selectedJob();
  if (!currentJob) return;

  const equipmentIndex = currentJob.equipment.findIndex(
    (eq) => eq.foremanEquipmentDispatchID === equipmentDispatchID
  );

  if (equipmentIndex > -1) {
    const updatedEquipment = {
      ...currentJob.equipment[equipmentIndex],
      ...updates,
    };

    if (updates.usages) {
      updatedEquipment.usages = updates.usages;
      updatedEquipment.totalHoursUsed = updatedEquipment.usages
        .filter((u) => u.bActive && !u.notUsed)
        .reduce((total, u) => {
          const quantity = Number(u.quantity || 1);
          const amount = Number(u.amount || 0);
          return total + quantity * amount;
        }, 0);
    }

    currentJob.equipment[equipmentIndex] = updatedEquipment;

    if (
      service.selectedEquipment()?.foremanEquipmentDispatchID ===
      equipmentDispatchID
    ) {
      service.selectedEquipmentId.set(
        updatedEquipment.equipmentDispatchID ||
          updatedEquipment.foremanEquipmentDispatchID
      );
    }
  }

  service.selectedJob.set({ ...currentJob });
}
